// ReportModal.jsx
import React, { useState } from 'react';
import { X } from 'lucide-react';
import { submitReport, getReportReasons } from './ReportHandler';
import { auth } from '../../firebase';
import './ReportModal.css';
import { toast } from 'react-hot-toast';

const ReportModal = ({ 
  contentId, 
  contentType, 
  reportedText, 
  reportedUserId, 
  parentId = null, 
  onClose, 
  additionalData = {} 
}) => {
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const reportReasons = getReportReasons();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!auth.currentUser) {
      toast.error('You must be logged in to report content');
      return;
    }

    if (reason === '') {
      toast.error('Please select a reason for reporting');
      return;
    }

    if (reason === 'Other' && otherReason.trim() === '') {
      toast.error('Please provide details for the "Other" reason');
      return;
    }

    setLoading(true);
    
    try {
      const finalReason = reason === 'Other' ? `Other: ${otherReason}` : reason;
      
      const result = await submitReport(
        contentId,
        contentType,
        reportedText,
        reportedUserId,
        auth.currentUser.uid,
        finalReason,
        parentId,
        additionalData
      );

      if (result.success) {
        setSubmitted(true);
        toast.success('Report submitted. Our AI and admin team will review this content.');
      } else {
        toast.error(`Error submitting report: ${result.error}`);
      }
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="report-modal-overlay">
      <div className="report-modal">
        <div className="report-modal-header">
          <h2>Report Content</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        
        <div className="report-modal-content">
          {submitted ? (
            <div className="report-success">
              <h3>Report Submitted</h3>
              <p className="success-message">
                Thank you for your report. Our AI system will review the content immediately, and our admin team will also conduct a human review.
              </p>
              <p className="success-message">
                You will be notified of the outcome. Reports help us maintain a safe and respectful community.
              </p>
              <button 
                className="close-btn" 
                onClick={onClose}
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="reported-content">
                <h3>Reported Content:</h3>
                <p className="content-preview">{reportedText}</p>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="reportReason">Reason for reporting:</label>
                  <select 
                    id="reportReason" 
                    value={reason} 
                    onChange={(e) => setReason(e.target.value)}
                    disabled={loading}
                    required
                  >
                    <option value="">Select a reason</option>
                    {reportReasons.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                
                {reason === 'Other' && (
                  <div className="form-group">
                    <label htmlFor="otherReason">Please specify:</label>
                    <textarea
                      id="otherReason"
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      disabled={loading}
                      placeholder="Please provide details about why you're reporting this content"
                      required
                      rows={3}
                    />
                  </div>
                )}
                
                <div className="report-actions">
                  <button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="submit-btn"
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Submit Report'}
                  </button>
                </div>
              </form>
              
              <div className="report-info">
                <p>
                  Reports are first analyzed by our AI system to ensure community guidelines are followed.
                  Additionally, all reports are reviewed by our admin team for final decision making.
                  Content that violates our policies will be removed.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportModal; 